import { FileResponse } from '@kcftech/prometheustypescriptapiclient';
import Rotator from 'exif-auto-rotate';
import { sentryAddBreadcrumb } from 'utils/sentry';

export const pictureMaxSizeBytes = 1024 * 1024 * 10; // 10 MB

export enum FileType {
    Json = 'application/json',
    Excel = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    WavAudio = 'audio/wav',
    Zip = 'application/zip',
    Pdf = 'application/pdf',
    Jpg = 'image/jpeg',
    Png = 'image/png',
    Webp = 'image/webp',
}

const fileTypeToExtension: Record<FileType, string> = {
    [FileType.Json]: 'json',
    [FileType.Excel]: 'xlsx',
    [FileType.WavAudio]: 'wav',
    [FileType.Zip]: 'zip',
    [FileType.Pdf]: 'pdf',
    [FileType.Jpg]: 'jpg',
    [FileType.Png]: 'png',
    [FileType.Webp]: 'webp',
};

const withExtension = (fileName: string, extension: string) =>
    fileName.endsWith(`.${extension}`) ? fileName : `${fileName}.${extension}`;

/**
 * Downloads a file using an anchor element.
 * @param file The file used for download.
 */
export function downloadFile(file: File) {
    const anchor = document.createElement('a');

    anchor.href = URL.createObjectURL(file).toString();
    anchor.download = file.name;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(anchor.href);
}

/**
 * Uploads a file using an anchor element. Use this only when uploading
 * a file tied to a button. For drag and drop support, use the kendo upload component.
 * @param onUpload function to call when the file should be uploaded
 * @param acceptableFileTypes the file types for the input element to accept
 */
export function uploadFile(
    onUpload: (file: File) => void,
    acceptableFileTypes: string[]
) {
    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.name = 'file';
    fileInput.accept = acceptableFileTypes.join(',');

    fileInput.onchange = () => {
        if (fileInput.files) {
            onUpload(fileInput.files[0]);
        }
    };

    fileInput.click();
}

/**
 * Parses a {@link Response}'s headers and returns a file name based on the
 * contents of 'content-disposition'. Undefined if said header does not exist.
 *
 * @param res A response object, as from `fetch`
 * @returns A string representing the file name or undefined.
 */
export function fileNameFromResponse(res: Response) {
    // Note, this matches how NSwag builds `FileResponse`s.
    const contentDisposition = res.headers.get('content-disposition');
    const fileNameMatch = contentDisposition
        ? /filename="?([^"]*?)"?(;|$)/g.exec(contentDisposition)
        : undefined;
    return fileNameMatch && fileNameMatch.length > 1 ? fileNameMatch[1] : undefined;
}

export type DownloadFileResponseOptions = Readonly<{
    response: FileResponse;
    defaultFileName: string;
    fileType: FileType;
}>;

/**
 * Initiates a download for a FileResponse object.
 */
export function downloadFileResponse({
    response,
    defaultFileName,
    fileType,
}: DownloadFileResponseOptions) {
    const baseFileName = response.fileName ?? defaultFileName;
    // some browsers (chromium) will auto-add the correct file extension
    // automatically. firefox, at time of writing, will not (at least not with
    // our download method), so this bit of logic adds the extension explicitly.
    const fileNameWithExtension = withExtension(
        baseFileName,
        fileTypeToExtension[fileType]
    );
    downloadFile(
        new File([response.data], fileNameWithExtension, {
            type: fileType,
        })
    );
}

export async function normalizeImageFileRotation(file: File): Promise<File> {
    try {
        const fileBlob = await Rotator.createRotatedImageAsync(file, 'blob');
        const normalizedFile = new File([fileBlob], file.name, { type: file.type });
        return normalizedFile;
    } catch (error: unknown) {
        sentryAddBreadcrumb({
            category: 'file.rotation',
            message: 'Image rotation normalization failed, using original file',
            level: 'warning',
            data: { fileName: file.name, fileSize: file.size, error: String(error) },
        });
        return file;
    }
}

export function isFileAJpeg(file: File): boolean {
    return file.type === FileType.Jpg;
}
